type ExprValue = boolean | number | string;

const tokenize = (expr: string): Array<string> => {
  if (!expr) return [];

  const cleaned = expr.replace(/\s+/g, "");

  try {
    const tokens = cleaned.match(/&&|\|\||!|\(|\)|"[^"]*"|\d+|true|false/g);
    return tokens ?? [];
  } catch {
    return [];
  }
};

const evaluateLogic = (expr: string): boolean => {
  const tokens = tokenize(expr);
  if (tokens.length === 0) return false;

  let pos = 0;

  const parsePrimary = (): ExprValue => {
    const token = tokens[pos++];

    if (!token) return false;

    if (token === "(") {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const value = parseExpression();
      if (tokens[pos++] !== ")") return false;
      return value;
    }

    if (token === "true") return true;
    if (token === "false") return false;
    if (!isNaN(Number(token))) return Number(token);
    if (/^".*"$/.test(token)) return token.slice(1, -1);

    return false;
  };

  const parseUnary = (): ExprValue => {
    if (tokens[pos] === "!") {
      pos++;
      return !parseUnary();
    }
    return parsePrimary();
  };

  const parseAnd = (): ExprValue => {
    let left = parseUnary();
    while (tokens[pos] === "&&") {
      pos++;
      const right = parseUnary();
      left = left && right;
    }
    return left;
  };

  const parseExpression = (): ExprValue => {
    let left = parseAnd();
    while (tokens[pos] === "||") {
      pos++;
      const right = parseAnd();
      left = left || right;
    }
    return left;
  };

  try {
    return Boolean(parseExpression());
  } catch (e) {
    console.error("[evaluateLogic] Failed to parse expression:", expr, e);
    return false;
  }
};

const normalizeLiteral = (value: string): ExprValue => {
  if (!value) return false;
  if (value === "true") return true;
  if (value === "false") return false;
  if (!isNaN(Number(value))) return Number(value);
  if (/^".*"$/.test(value)) return value.slice(1, -1);
  return value;
};

const evaluateIfElse = (expr: string): ExprValue => {
  if (!expr.startsWith("if")) return false;

  const match = expr.match(/^if\s*\((.*)\)\s*\{(.*)\}\s*else\s*\{?(.*)\}?$/s);

  if (!match) return false;

  const conditionStr = match[1]?.trim() ?? "";
  const trueBlock = match[2]?.trim().replace(/^return\s+/, "") ?? "";
  const falseBlock = match[3]?.trim().replace(/^return\s+/, "") ?? "";

  const condition = evaluateLogic(conditionStr);
  const result = condition ? trueBlock : falseBlock;

  return normalizeLiteral(result);
};

/**
 * парсит выражение  вида:  '!!123 && false' в false
 * парсит выражение  вида:  'if(1) {return "hi"} else 0' в "hi"
 * парсит выражение  вида:  'if(!0 && true) {1} else {0}' в 1
 * парсит выражение  вида:  'if("x") {return true} else false' в true
 */
export const evaluateCmsExpression = (expr: string): ExprValue => {
  if (!expr || typeof expr !== "string") return false;

  const trimmed = expr.trim();
  if (!trimmed) return false;

  try {
    if (trimmed.startsWith("if")) return evaluateIfElse(trimmed);

    return evaluateLogic(trimmed);
  } catch (e) {
    console.error("[evaluateLogic] Failed to evaluate expression:", expr, e);
    return false;
  }
};
